// Text measuring for the printed page, so long office names, titles and
// flavor names are sized to fit instead of being cut off or running into
// each other. Uses a canvas (it knows the loaded web fonts); without one
// (old browsers, tests) it falls back to a generous estimate.

export type Measure = (text: string, font: string) => number;

const cache = new Map<string, number>();
let context: CanvasRenderingContext2D | null | undefined;

const canvasMeasure: Measure = (text, font) => {
  if (context === undefined) {
    try {
      context = document.createElement('canvas').getContext('2d');
    } catch {
      context = null;
    }
  }
  if (!context) return estimateWidth(text, font);
  context.font = font;
  return context.measureText(text).width;
};

/** Bold display faces run about 0.62em a character; overestimating is the safe side. */
export const estimateWidth = (text: string, font: string): number => {
  const match = /(\d+(?:\.\d+)?)(pt|px)/.exec(font);
  const size = match ? parseFloat(match[1]) * (match[2] === 'pt' ? 4 / 3 : 1) : 16;
  return text.length * size * 0.62;
};

let measureImpl: Measure = canvasMeasure;

/** Width in CSS px of `text` drawn in a CSS `font` shorthand. */
export const measureText: Measure = (text, font) => {
  const key = `${font}\u0000${text}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const width = measureImpl(text, font);
  cache.set(key, width);
  return width;
};

/** Forget measurements, e.g. after a web font finishes loading. */
export const clearTextCache = () => cache.clear();

/** Swap the measuring function (tests). Returns a function that restores it. */
export const setMeasure = (measure: Measure) => {
  const previous = measureImpl;
  measureImpl = measure;
  cache.clear();
  return () => {
    measureImpl = previous;
    cache.clear();
  };
};

export const boldFont = (sizePt: number, stack: string) => `bold ${sizePt}pt ${stack}`;

/** Widest single word: names wrap between words, never inside one. */
export const longestWordWidth = (text: string, sizePt: number, stack: string): number =>
  Math.max(0, ...text.split(/\s+/).filter(Boolean).map(word => measureText(word, boldFont(sizePt, stack))));

/**
 * The largest size up to `sizePt` at which `text` fits `maxWidth` on one
 * line, never below `minRatio` of the chosen size. `fits` is false when even
 * the smallest size is too wide, so the caller can let it wrap.
 */
export const fitFontSize = (
  text: string,
  sizePt: number,
  stack: string,
  maxWidth: number,
  minRatio = 0.55,
): { sizePt: number; fits: boolean } => {
  if (!text.trim() || maxWidth <= 0) return { sizePt, fits: true };
  const width = measureText(text, boldFont(sizePt, stack));
  if (width <= maxWidth) return { sizePt, fits: true };
  const min = sizePt * minRatio;
  // Width grows linearly with size; step down a little more to be safe.
  const scaled = Math.floor((sizePt * maxWidth * 0.98) / width * 10) / 10;
  if (scaled >= min) return { sizePt: scaled, fits: true };
  return { sizePt: Math.round(min * 10) / 10, fits: false };
};
